import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth } from './AuthContext';
import { RootState, AppDispatch } from '../store/store';
import { loadCurrentUserProfile } from '../store/map3DataSlice';
import { ensureCurrentUserHuman } from '../store/map3PendingSlice';
import { useNavigate, useLocation } from 'react-router-dom';

export function UserProfileLoader({ children }: { children: React.ReactNode }) {
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Use only the new state
  const userProfile = useSelector((state: RootState) => state.map3Data.userProfile);

  const accountType = userProfile.accountType;
  const isPending = accountType === 'alpha_pending';
  const isAllowedForPending =
    location.pathname.includes('/sign-in') ||
    location.pathname.includes('/sign-up') ||
    location.pathname.includes('/complete-signup') ||
    location.pathname.includes('/pending');

  // Track the uid we already ensured a Human for, to avoid re-dispatching.
  const ensuredHumanUidRef = useRef<string | null>(null);

  useEffect(() => {
    // Check if we need to load the profile:
    // 1. We have an authenticated user
    // 2. Either we don't have a profile, or the profile UIDs don't match
    if (user?.uid && (!userProfile?.uid || userProfile.uid !== user.uid)) {
      dispatch(loadCurrentUserProfile());
    }
  }, [dispatch, user, userProfile?.uid]);

  useEffect(() => {
    // After profile is loaded, ensure a Human document exists for the current user.
    // The thunk is idempotent — checks Redux state and Firestore before creating.
    if (userProfile?.uid && ensuredHumanUidRef.current !== userProfile.uid) {
      ensuredHumanUidRef.current = userProfile.uid;
      dispatch(ensureCurrentUserHuman()).catch(() => {
        // Reset so the effect can retry on next profile change
        ensuredHumanUidRef.current = null;
      });
    }
  }, [dispatch, userProfile?.uid]);

  useEffect(() => {
    // TODO: This is a hack. Relying on Redux for user credentials in this causes trouble.
    // Replace this when proper roles and permissions are in place.
    // ALSO NOTE: State defaults to 'alpha_deactivated' for unauthenticated users and before state loads.
    // This ^^ means applying the same trick to deactivated accounts doesn't work. Admins, members, and guests
    // all end up getting redirected and tangled in this mess if we do that. (See previous TODO.)
    if (isPending && !isAllowedForPending) {
      navigate('/pending');
    }
  }, [user, isPending, navigate, isAllowedForPending]);

  return <>{children}</>;
}
